import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const EventsSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const allEvents = data?.events || [];
  const layout = data?.layout || 'list';
  const spacing = data?.spacing || 'spaced';

  // Auto-archive: split past vs upcoming (undated events stay upcoming)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPast = (ev) => {
    if (ev.is_past === true) return true;
    if (!ev.date) return false;
    const d = new Date(ev.date);
    return !isNaN(d.getTime()) && d < today;
  };
  const byDate = (a, b) => new Date(a.date || '2999-12-31') - new Date(b.date || '2999-12-31');
  const events = allEvents.filter((ev) => !isPast(ev)).sort(byDate);
  const pastEvents = allEvents.filter(isPast).sort((a, b) => new Date(b.date || '1900-01-01') - new Date(a.date || '1900-01-01'));
  const posterPast = pastEvents.filter((ev) => ev.image);
  const textPast = pastEvents.filter((ev) => !ev.image);
  const showPast = data?.past_events_visible === true && pastEvents.length > 0;
  const pastLayout = data?.past_layout || 'wall';

  if (allEvents.length === 0) return null;

  // Format date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return { day: '--', month: '---' };
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()
    };
  };

  const gapClass = spacing === 'compact' ? 'gap-2' : 'gap-4';

  // List layout - horizontal cards
  const renderListLayout = () => (
    <div className={cn("space-y-0", spacing === 'spaced' ? 'space-y-4' : 'divide-y', t.border)}>
      {events.map((event, idx) => (
        <div 
          key={event.id || idx} 
          className={cn(
            "flex flex-col sm:flex-row items-start sm:items-center justify-between p-6",
            spacing === 'spaced' && "rounded-xl border",
            spacing === 'spaced' && t.border,
            t.bg
          )}
        >
          <div className="flex items-center gap-4 mb-4 sm:mb-0">
            {event.image && (
              <img src={event.image} alt={event.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 hidden sm:block" />
            )}
            <div 
              className="text-center px-4 py-2 rounded-lg min-w-[100px]"
              style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
            >
              <div className="text-sm font-medium">{formatEventDate(event.date)}</div>
              {event.time && <div className="text-xs opacity-75">{event.time}</div>}
            </div>
            <div>
              <h3 className={cn("font-semibold", t.text)}>{event.title}</h3>
              <p className={t.textMuted}>{event.venue}{event.location ? ` • ${event.location}` : ''}</p>
              {event.description && (
                <p className={cn("text-sm mt-1", t.textMuted)}>{event.description}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {event.show_tickets !== false && event.ticket_url && (
              <Button 
                className="text-white"
                style={{ backgroundColor: primaryColor }}
                onClick={() => window.open(event.ticket_url, '_blank')}
                data-testid={`event-tickets-btn-${idx}`}
              >
                Get Tickets
              </Button>
            )}
            {event.show_more_info && event.more_info_url && (
              <Button 
                variant="outline"
                className={cn("border", t.border)}
                style={{ color: primaryColor, borderColor: primaryColor }}
                onClick={() => window.open(event.more_info_url, '_blank')}
                data-testid={`event-info-btn-${idx}`}
              >
                More Info
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Grid layout - card grid
  const renderGridLayout = () => (
    <div className={cn("grid md:grid-cols-2 lg:grid-cols-3", gapClass)}>
      {events.map((event, idx) => {
        const { day, month } = formatShortDate(event.date);
        return (
          <div 
            key={event.id || idx} 
            className={cn(
              "p-5 rounded-xl border flex flex-col",
              t.border,
              t.bg
            )}
          >
            {event.image && (
              <img src={event.image} alt={event.title} className="w-full h-36 rounded-lg object-cover mb-4" />
            )}
            <div className="flex items-start gap-4 mb-4">
              <div 
                className="text-center px-3 py-2 rounded-lg"
                style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
              >
                <div className="text-2xl font-bold">{day}</div>
                <div className="text-xs font-medium">{month}</div>
              </div>
              <div className="flex-1">
                <h3 className={cn("font-semibold", t.text)}>{event.title}</h3>
                <p className={cn("text-sm", t.textMuted)}>{event.venue}</p>
                {event.location && (
                  <p className={cn("text-xs", t.textMuted)}>{event.location}</p>
                )}
              </div>
            </div>
            {event.time && (
              <p className={cn("text-sm mb-3", t.textMuted)}>Time: {event.time}</p>
            )}
            {event.description && (
              <p className={cn("text-sm mb-4 line-clamp-2", t.textMuted)}>{event.description}</p>
            )}
            <div className="flex gap-2 mt-auto">
              {event.show_tickets !== false && event.ticket_url && (
                <Button 
                  className="text-white flex-1"
                  size="sm"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => window.open(event.ticket_url, '_blank')}
                >
                  Tickets
                </Button>
              )}
              {event.show_more_info && event.more_info_url && (
                <Button 
                  variant="outline"
                  size="sm"
                  className={cn("border flex-1", t.border)}
                  style={{ color: primaryColor, borderColor: primaryColor }}
                  onClick={() => window.open(event.more_info_url, '_blank')}
                >
                  Info
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Timeline layout - vertical timeline
  const renderTimelineLayout = () => (
    <div className="relative">
      {/* Timeline line */}
      <div 
        className="absolute left-[60px] top-0 bottom-0 w-0.5"
        style={{ backgroundColor: primaryColor + '30' }}
      />
      
      <div className={cn("space-y-0", spacing === 'spaced' ? 'space-y-6' : 'space-y-2')}>
        {events.map((event, idx) => {
          const { day, month } = formatShortDate(event.date);
          return (
            <div 
              key={event.id || idx} 
              className="flex items-start gap-6 relative"
            >
              {/* Date circle */}
              <div 
                className="relative z-10 w-[80px] flex-shrink-0 text-center py-2 rounded-lg"
                style={{ backgroundColor: primaryColor + '20', color: primaryColor }}
              >
                <div className="text-xl font-bold">{day}</div>
                <div className="text-xs font-medium">{month}</div>
              </div>
              
              {/* Event card */}
              <div 
                className={cn(
                  "flex-1 p-5 rounded-xl border",
                  t.border,
                  t.bg
                )}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {event.image && (
                      <img src={event.image} alt={event.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 hidden sm:block" />
                    )}
                    <div>
                      <h3 className={cn("font-semibold", t.text)}>{event.title}</h3>
                      <p className={t.textMuted}>{event.venue}{event.location ? ` • ${event.location}` : ''}</p>
                      {event.time && (
                        <p className={cn("text-sm mt-1", t.textMuted)}>Time: {event.time}</p>
                      )}
                      {event.description && (
                        <p className={cn("text-sm mt-2", t.textMuted)}>{event.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {event.show_tickets !== false && event.ticket_url && (
                      <Button 
                        className="text-white"
                        size="sm"
                        style={{ backgroundColor: primaryColor }}
                        onClick={() => window.open(event.ticket_url, '_blank')}
                      >
                        Get Tickets
                      </Button>
                    )}
                    {event.show_more_info && event.more_info_url && (
                      <Button 
                        variant="outline"
                        size="sm"
                        className={cn("border", t.border)}
                        style={{ color: primaryColor, borderColor: primaryColor }}
                        onClick={() => window.open(event.more_info_url, '_blank')}
                      >
                        More Info
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <section id="events" className={cn("py-20 px-4", t.card)}>
      <div className={cn("mx-auto", layout === 'grid' || posterPast.length > 0 ? "max-w-6xl" : "max-w-4xl")}>
        <AnimatedSection animation="fadeUp" className="text-center mb-12">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            What's On
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'Tour Dates'}
          </h2>
        </AnimatedSection>
        {events.length === 0 && (
          <p className={cn("text-center py-8", t.textMuted)} data-testid="no-upcoming-events">
            No upcoming shows right now — check back soon!
          </p>
        )}
        {events.length > 0 && layout === 'list' && renderListLayout()}
        {events.length > 0 && layout === 'grid' && renderGridLayout()}
        {events.length > 0 && layout === 'timeline' && renderTimelineLayout()}

        {showPast && (
          <div className="mt-16" data-testid="past-events-section">
            <h3 className={cn("text-2xl font-bold mb-8 text-center opacity-70", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>Past Shows</h3>
            {posterPast.length > 0 && pastLayout === 'carousel' && (
              <Carousel opts={{ align: 'start', loop: posterPast.length > 4 }} data-testid="past-posters-carousel">
                <CarouselContent className="-ml-5">
                  {posterPast.map((event, idx) => (
                    <CarouselItem key={event.id || idx} className="pl-5 basis-3/4 sm:basis-1/2 md:basis-1/3 lg:basis-1/4" data-testid={`past-poster-${idx}`}>
                      <div className="group">
                        <div className={cn("rounded-xl overflow-hidden border aspect-[2/3]", t.border, t.bg)}>
                          <img
                            src={event.image}
                            alt={event.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <p className={cn("mt-2.5 text-sm font-semibold leading-snug", t.text)}>{event.title}</p>
                        {(event.date || event.venue) && (
                          <p className={cn("text-xs mt-0.5", t.textMuted)}>
                            {event.date ? formatEventDate(event.date) : ''}{event.date && event.venue ? ' • ' : ''}{event.venue || ''}
                          </p>
                        )}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex items-center justify-center gap-3 mt-8">
                  <CarouselPrevious
                    className={cn("static translate-y-0 h-10 w-10 bg-transparent", t.border, t.text)}
                    style={{ borderColor: primaryColor + '60' }}
                    data-testid="past-carousel-prev"
                  />
                  <CarouselNext
                    className={cn("static translate-y-0 h-10 w-10 bg-transparent", t.border, t.text)}
                    style={{ borderColor: primaryColor + '60' }}
                    data-testid="past-carousel-next"
                  />
                </div>
              </Carousel>
            )}
            {posterPast.length > 0 && pastLayout !== 'carousel' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="past-posters-grid">
                {posterPast.map((event, idx) => (
                  <div key={event.id || idx} className="group" data-testid={`past-poster-${idx}`}>
                    <div className={cn("rounded-xl overflow-hidden border aspect-[2/3]", t.border, t.bg)}>
                      <img
                        src={event.image}
                        alt={event.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <p className={cn("mt-2.5 text-sm font-semibold leading-snug", t.text)}>{event.title}</p>
                    {(event.date || event.venue) && (
                      <p className={cn("text-xs mt-0.5", t.textMuted)}>
                        {event.date ? formatEventDate(event.date) : ''}{event.date && event.venue ? ' • ' : ''}{event.venue || ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {textPast.length > 0 && (
              <div className={cn("divide-y opacity-60", t.border, posterPast.length > 0 && "mt-10")}>
                {textPast.map((event, idx) => (
                  <div key={event.id || idx} className="flex items-center justify-between py-3 px-2 gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      {event.date && <span className={cn("text-sm font-mono whitespace-nowrap", t.textMuted)}>{formatEventDate(event.date)}</span>}
                      <p className={cn("text-sm truncate", t.text)}>
                        {event.title}{event.venue ? ` — ${event.venue}` : ''}{event.location ? `, ${event.location}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};


